import { Component, ChangeDetectionStrategy } from '@angular/core';

import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

import { SuiBadgeVariant } from '@sensika/sui/badge';

/** cellRendererParams contract for AgBadgeCellRendererComponent. */
export interface AgBadgeCellRendererParams {
  /**
   * Row field holding the value to render - a nomenclature ({ id, name }) or
   * a legacy action tag ({ value, style }).
   */
  nomField: string;
  /**
   * Property on that object carrying the badge label. Default 'name';
   * legacy action tags carry it in 'value'.
   */
  textKey?: string;
  /**
   * Maps the field's object (and full row) to a badge variant; omit for a
   * neutral chip (e.g. tag-like columns). Views pass their domain map, e.g.
   * `(nom) => harvestStatusVariant(nomVariantKey(nom))` or
   * `(action) => actionTagVariant(action.style)`.
   */
  variantOf?: (item: any, data: unknown) => SuiBadgeVariant;
}

/**
 * The one grid cell renderer for nomenclature badges/chips (status, outcome,
 * mode, action tags) - every status-like column uses it so badges look the
 * same on every grid. The badge stretches to the cell width with a centered
 * label; empty values render as a dash.
 */
@Component({
  selector: 'app-ag-badge-cell-renderer',
  templateUrl: './ag-badge-cell-renderer.component.html',
  styleUrl: './ag-badge-cell-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AgBadgeCellRendererComponent implements ICellRendererAngularComp {
  item: any = null;
  text = '';
  variant: SuiBadgeVariant = 'neutral';

  agInit(params: ICellRendererParams): void {
    this.update(params);
  }

  refresh(params: ICellRendererParams): boolean {
    this.update(params);
    return true;
  }

  private update(params: ICellRendererParams): void {
    const { nomField, textKey, variantOf } = params as ICellRendererParams &
      AgBadgeCellRendererParams;
    this.item = params.data?.[nomField] ?? null;
    this.text = this.item?.[textKey ?? 'name'] ?? '';
    this.variant =
      this.item && variantOf ? variantOf(this.item, params.data) : 'neutral';
  }
}
